import { Choice, ChoiceProps, HeaderModal, HiddenResult, MultipleChoicePopup, ReactIcons, SurveyUtils } from "@kalyzee/escaet-ui";
import { useEffect, useMemo, useState } from "react";
import { useSocketAppDispatch } from "../../hooks/app";
import { useMeetingSelector } from "../../hooks/meeting";
import { ForAgainstNoOpinion, SurveyAnswerType, SurveyType } from "../../interfaces/survey";
import socketSurveyActions from "../../store/survey/actions";

import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk";
import { SurveyStatus } from "@kalyzee/kast-web-sdk/websocket";
import { usePopulatedMeetingData } from "../../hooks/populatedData";
import styles from "./SurveyExpertContainer.module.css";
import { SurveyResultsViewer } from "./SurveyResultsViewer";

const generateMultipleChoices = (number: number) => {
  return Array.from({ length: number }, (_, i) => ({
    content: String.fromCharCode(65 + i), // Generates 'A', 'B', 'C', etc.
  }));
};

type ChoiceData = ChoiceProps["data"][number] & {
  name: string;
  disabled: boolean;
  type: SurveyType;
  question: {
    content: any; // TODO : export SurveyCreateQuestion from websocket module
    minChoiceNumber: any;
    maxChoiceNumber: any;
    choices: any;
  };
};

const ALLOW_HALF_STARTS = false;

const ChoiceDataArray: ChoiceData[] = [
  {
    name: "Oui / Non",
    disabled: false,
    type: SurveyType.YesNo,
    question: {
      content: "Répondre à l'enquête",
      minChoiceNumber: 1,
      maxChoiceNumber: 1,
      choices: [[{ content: SurveyAnswerType.Yes }, { content: SurveyAnswerType.No }]],
    },
    color: "#688EA4",
    backgroundColor: "#D0EEFF",
  },
  {
    name: "Vrai / Faux",
    disabled: false,
    type: SurveyType.TrueFalse,
    question: {
      content: "Répondre à l'enquête",
      minChoiceNumber: 1,
      maxChoiceNumber: 1,
      choices: [[{ content: SurveyAnswerType.True }, { content: SurveyAnswerType.False }]],
    },
    color: "#86A6FF",
    backgroundColor: "#E4EAFF",
  },
  {
    name: "Pour / Contre / Ne se prononce pas",
    disabled: false,
    type: SurveyType.ForAgainstNoOpinion,
    question: {
      content: "Répondre à l'enquête",
      minChoiceNumber: 1,
      maxChoiceNumber: 1,
      choices: [[{ content: ForAgainstNoOpinion.For }, { content: ForAgainstNoOpinion.Against }, { content: ForAgainstNoOpinion.NoOpinion }]],
    },
    color: "#6667FF",
    backgroundColor: "#CCCEFF",
  },
  {
    name: "Question à choix multiples (ABCD)",
    disabled: false,
    type: SurveyType.MultipleChoice,
    question: {
      content: "Répondre à l'enquête",
      minChoiceNumber: 1,
      maxChoiceNumber: 1,
      choices: [generateMultipleChoices(3), generateMultipleChoices(4), generateMultipleChoices(5), generateMultipleChoices(6)],
    },
    color: "#6DD4B2",
    backgroundColor: "#DAF5ED",
  },
  {
    name: "Étoiles",
    disabled: false,
    type: SurveyType.Stars,
    question: {
      content: "Répondre à l'enquête",
      minChoiceNumber: 1,
      maxChoiceNumber: 1,
      choices: [
        ALLOW_HALF_STARTS
          ? [
              { content: "0.5" },
              { content: "1" },
              { content: "1.5" },
              { content: "2" },
              { content: "2.5" },
              { content: "3" },
              { content: "3.5" },
              { content: "4" },
              { content: "4.5" },
              { content: "5" },
            ]
          : [{ content: "1" }, { content: "2" }, { content: "3" }, { content: "4" }, { content: "5" }],
      ],
    },
    color: "#E143D9",
    backgroundColor: "#FBE6FA",
  },
];

const MultipleChoiceDatas = ChoiceDataArray[3]!.question.choices.map((choice: any) => {
  return {
    choice: `${choice.length} choix`,
    description: choice.map((c: any) => c.content).join(", "),
  };
});

export const randomColor = () => {
  const avaiableColors = ["#A2C4D8", "#C0ADD5", "#D994AC", "#FF8D83", "#FFF183", "#83C1FF", "#83FF88"];
  return avaiableColors[Math.floor(Math.random() * avaiableColors.length)];
};

export interface SurveyExpertContainerProps {
  meetingContainerAPI?: MeetingContainerAPI;
  style?: React.CSSProperties;
}

export const SurveyExpertContainer = ({ meetingContainerAPI, style }: SurveyExpertContainerProps) => {
  const dispatch = useSocketAppDispatch();

  // multi survey not supported yet, we take the last pushed one
  const [currentSurvey] = useMeetingSelector(meetingContainerAPI, (state) => state.surveys[state.surveys.length - 1]);
  const [meetingId] = useMeetingSelector(meetingContainerAPI, (state) => state.meeting?.id);
  const populatedData = usePopulatedMeetingData(meetingContainerAPI);

  const surveyAnswers = useMemo<number>(() => {
    if (!currentSurvey) return 0;
    const userIds = new Set<string>();
    currentSurvey.questions.forEach((question) => {
      question.choices.forEach((choice) => {
        choice.votesUserIds?.forEach((userId) => {
          userIds.add(userId);
        });
      });
    });
    return userIds.size;
  }, [currentSurvey]);
  const totalUsers = populatedData?.studentUsers.length || 0;

  const [choice, setChoice] = useState<SurveyType>();
  // For multiple choice surveys :
  const [advancedChoiceDraft, setAdvancedChoiceDraft] = useState<number | undefined>(0);
  const [advancedChoice, setAdvancedChoice] = useState<number>();

  const onCloseSurvey = async () => {
    if (!currentSurvey) return;
    await dispatch(socketSurveyActions.close({ surveyId: currentSurvey.id }));
  };

  const onDeleteSurvey = async () => {
    if (!currentSurvey) return;
    await dispatch(socketSurveyActions.delete({ surveyId: currentSurvey.id }));
  };

  useEffect(() => {
    if (!meetingId) return;
    if (!choice) return;
    if (choice === SurveyType.MultipleChoice && advancedChoice === undefined) return;
    const data = ChoiceDataArray.find((c) => c.type === choice);
    if (!data) return;
    const idx = advancedChoice ?? 0;
    dispatch(
      socketSurveyActions.create({
        meetingId,
        name: data.name,
        questions: [
          {
            ...data.question,
            choices: data.question.choices[idx] ?? [],
          },
        ],
      })
    );
  }, [meetingId, choice, advancedChoice]);

  useEffect(() => {
    if (!currentSurvey) {
      setChoice(undefined);
      setAdvancedChoice(undefined);
      return;
    }
  }, [currentSurvey]);

  const onSelectSurveyType = (type: SurveyType) => {
    setChoice(type);
  };

  //////////////////////////////////
  // Rendering depending on state //
  //////////////////////////////////

  const renderContent = (children: React.ReactNode, boxed = true) => {
    return (
      <div className={styles.container} style={{ position: "relative", ...(style || {}) }}>
        {boxed ? (
          <>
            <div style={{ flex: 2, marginTop: 44, marginLeft: 44 }}>
              <HeaderModal title="Mon sondage" icon={<ReactIcons.Pie style={{ width: 32, height: 32 }} />} />
            </div>
            <div style={{ flex: 3, minWidth: 0, height: "100%" }}>
              <div className={styles.box}>{children}</div>
            </div>
          </>
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            {children}
          </div>
        )}
      </div>
    );
  };

  if (currentSurvey) {
    if (currentSurvey.status === SurveyStatus.Open) {
      return renderContent(
        <HiddenResult
          answer={surveyAnswers}
          totalParticipants={totalUsers}
          sondageStep={surveyAnswers === totalUsers ? 2 : 1}
          onPublishResult={onCloseSurvey}
          startedAt={currentSurvey.createdAt}
        />
      );
    }
    return renderContent(
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "1em",
        }}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
            marginTop: 60,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <SurveyResultsViewer survey={currentSurvey} />
        </div>
        <div className={styles.closeButtonWrapper}>
          <SurveyUtils.Button onClick={onDeleteSurvey}>Fermer le sondage</SurveyUtils.Button>
        </div>
      </div>,
      false
    );
  }

  if (!choice) {
    return renderContent(<Choice data={ChoiceDataArray} onSelectSurveyTypeAndCreate={(data) => onSelectSurveyType(data.type as SurveyType)} />);
  }

  if (choice === SurveyType.MultipleChoice && advancedChoice === undefined) {
    return renderContent(
      <div className={styles.defineSurveyOptionContainer}>
        <MultipleChoicePopup
          data={MultipleChoiceDatas}
          selectedIndex={advancedChoiceDraft}
          onSelectIndex={(index) => setAdvancedChoiceDraft(index)}
          onClose={() => {
            setChoice(undefined);
            setAdvancedChoiceDraft(undefined);
          }}
          onClick={() => setAdvancedChoice(advancedChoiceDraft)}
        />
      </div>
    );
  }

  return renderContent(<div>Loading ...</div>);
};

export default SurveyExpertContainer;
